import { useState, useRef, useEffect } from 'react';
import { Bell, Heart, MessageCircle, Reply, X, UserPlus, DollarSign, Users, Sparkles, Gift } from 'lucide-react';
import useNotifications from '../../hooks/useNotifications';
import { useTranslation } from 'react-i18next';

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function notifIcon(type: string) {
  switch (type) {
    case 'reaction':
    case 'scoop_like': return <Heart size={14} className="text-red-400" />;
    case 'comment': return <MessageCircle size={14} className="text-blue-400" />;
    case 'reply': return <Reply size={14} className="text-iris-400" />;
    case 'follow': return <UserPlus size={14} className="text-emerald-400" />;
    case 'tip': return <DollarSign size={14} className="text-amber-400" />;
    case 'friend_request':
    case 'friend_accepted':
    case 'face_link_request': return <Users size={14} className="text-iris-400" />;
    case 'welcome': return <Sparkles size={14} className="text-iris-400" />;
    case 'reward': return <Gift size={14} className="text-amber-400" />;
    case 'system': return <Bell size={14} className="text-blue-400" />;
    default: return <Bell size={14} className="text-white/50" />;
  }
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const { unreadCount, notifications, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/6 transition-colors"
        title={t('social.notifications')}
      >
        <Bell size={18} className="text-white/60" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-[#0c0c14] border border-white/8 rounded-xl shadow-[0_24px_60px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.02)] z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/6 bg-linear-to-r/srgb from-iris-500/6 to-transparent flex-wrap gap-y-2">
            <h3 className="text-sm font-semibold text-white">{t('social.notifications')}</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[10px] text-iris-400 hover:text-iris-300 transition-colors"
                >
                  {t('social.markAllRead')}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white/60">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <Bell size={24} className="mx-auto mb-2 text-white/15" />
                <p className="text-xs text-white/30">{t('social.noNotificationsYet')}</p>
              </div>
            ) : (
              notifications.map((n: any) => {
                const isRead = n.is_read || n.isRead;
                return (
                  <button
                    key={n.id}
                    onClick={() => {
                      if (!isRead) markRead(n.id);
                      // Every notification navigates somewhere — the platform
                      // convention (LinkedIn, X): a target when it has one,
                      // the notifications page to read the full message when
                      // it doesn't. A click that only marked-read looked dead.
                      const isSystem = ['welcome', 'reward', 'system'].includes(n.type);
                      const href = (!isSystem && (n.link || n.targetUrl)) || '/dashboard/notifications';
                      {
                        setOpen(false);
                        // Add locale prefix if relative path
                        const locale = window.location.pathname.split('/')[1] || 'en';
                        const fullHref = href.startsWith('/') && !href.startsWith(`/${locale}`) ? `/${locale}${href}` : href;
                        try {
                          const url = new URL(fullHref, window.location.origin);
                          if (url.origin === window.location.origin || url.protocol === 'https:') {
                            window.location.href = url.href;
                          }
                        } catch { /* ignore invalid URLs */ }
                      }
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-white/3 transition-colors flex items-start gap-3 ${
                      !isRead ? 'bg-iris-500/4' : ''
                    }`}
                  >
                    <div className="mt-0.5 shrink-0">
                      {notifIcon(n.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-relaxed ${!isRead ? 'text-white' : 'text-white/60'}`}>
                        {n.title || n.actorName}
                      </p>
                      {(n.body || n.previewText) && (
                        <p className="text-[10px] text-white/30 truncate mt-0.5">{n.body || n.previewText}</p>
                      )}
                      <p className="text-[10px] text-white/20 mt-1">{timeAgo(n.created_at || n.createdAt)}</p>
                    </div>
                    {!isRead && (
                      <div className="w-2 h-2 rounded-full bg-iris-500 shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })
            )}
          </div>
          <a
            href={`/${window.location.pathname.split('/')[1] || 'en'}/dashboard/notifications`}
            className="block text-center text-xs text-iris-300 hover:text-iris-200 py-2.5 border-t border-white/6"
            onClick={() => setOpen(false)}
          >
            {t('social.viewAllNotifications', 'View all notifications')}
          </a>
        </div>
      )}
    </div>
  );
}
